/* Calm collection: progressive enhancement, no external libraries.
   Real product/variant IDs and Shopify's native forms remain the source of truth. */
(() => {
  'use strict';
  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => [...root.querySelectorAll(selector)];
  // Keep product format separate from the storefront's page-format parameter.
  const FORMAT_PARAM = 'gbm_format';
  const normaliseFormat = value => {
    const format = String(value || '').trim().toLowerCase();
    if (format === 'capsule' || format === 'capsules') return 'capsules';
    if (format === 'powder' || format === 'powders') return 'powder';
    return '';
  };
  const key = value => String(value || '').toLowerCase().replace(/['’‘]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').replace('lion-s-mane', 'lions-mane');

  class GbmHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      const menus = $$(this, '[data-menu]');
      menus.forEach(menu => menu.addEventListener('toggle', () => {
        if (!menu.open) return;
        menus.forEach(other => { if (other !== menu) other.open = false; });
        if (menu.classList.contains('gbm-search')) $(menu, 'input[type=search]')?.focus();
      }, {signal}));
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) menus.forEach(menu => { menu.open = false; });
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const active = menus.find(menu => menu.open);
        if (active) { active.open = false; $(active, 'summary')?.focus(); event.preventDefault(); }
      }, {signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class GbmCatalog extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.mushroom = $(this, '[data-mushroom-filter]');
      this.format = $(this, '[data-format-filter]');
      this.complete = Number(this.dataset.pageCount || 1) <= 1;
      if (!this.mushroom || !this.format) return;
      this.captureCards();
      const change = () => { this.apply(); this.saveURL(true); };
      this.mushroom.addEventListener('change', change, {signal});
      this.format.addEventListener('change', change, {signal});
      $$(this, '[data-clear-filters]').forEach(button => button.addEventListener('click', () => {
        this.mushroom.value = ''; this.format.value = ''; change();
        this.mushroom.focus({preventScroll: true});
      }, {signal}));
      window.addEventListener('popstate', () => this.readURL(), {signal});
      $(this, '[data-filterbar]').hidden = false;
      this.readURL(); this.loadCompleteCatalog(signal);
    }
    captureCards() {
      this.cards = $$(this, '[data-gbm-card]');
      const options = new Map();
      this.cards.forEach(card => {
        const image = $(card, '.gbm-card-image');
        card.original = card.original || {
          price: $(card, '[data-card-price]')?.textContent || '',
          format: $(card, '[data-card-format]')?.textContent || '',
          href: $(card, '[data-card-link]')?.getAttribute('href') || '',
          src: image?.getAttribute('src') || '', srcset: image?.getAttribute('srcset') || '',
          available: card.dataset.available !== 'false'
        };
        options.set(card.dataset.mushroom, card.dataset.species);
      });
      this.mushroom.replaceChildren(new Option('All mushrooms', ''));
      options.forEach((name, value) => this.mushroom.add(new Option(name, value)));
    }
    async loadCompleteCatalog(signal) {
      if (!window.GbmDiscovery || !this.dataset.catalogUrl) return;
      const notice = $(this, '[data-catalog-notice]');
      if (notice && !this.complete) notice.textContent = 'Loading the rest of the collection…';
      try {
        const catalog = await window.GbmDiscovery.ready(this.dataset.catalogUrl);
        if (signal.aborted || !this.isConnected) return;
        if (catalog.groups.length) {
          const fragment = document.createDocumentFragment();
          catalog.groups.forEach(group => {
            const card = window.GbmDiscovery.makeCard(group);
            if (card) fragment.append(card);
          });
          $(this, '[data-shop-grid]').replaceChildren(fragment);
        }
        this.complete = true;
        if (notice) notice.hidden = true;
        const pages = this.closest('.gbm-shop')?.querySelector('.gbm-pagination');
        if (pages) pages.hidden = true;
        this.captureCards(); this.readURL();
      } catch (error) {
        if (signal.aborted) return;
        if (notice) {
          notice.hidden = false;
          notice.textContent = 'The rest of the collection could not be loaded. Filters cover this page only; use the page links below to browse more products.';
        }
        this.apply();
        console.warn('Full catalog enhancement unavailable; native pagination remains active.', error);
      }
    }
    readURL() {
      const params = new URL(window.location.href).searchParams;
      const requested = key(params.get('mushroom'));
      // Do not silently turn an unknown mushroom deep link into "all products".
      if (requested && ![...this.mushroom.options].some(option => option.value === requested)) {
        this.mushroom.add(new Option(params.get('mushroom').replace(/-/g, ' '), requested));
      }
      this.mushroom.value = requested;
      this.format.value = normaliseFormat(params.get(FORMAT_PARAM) ?? params.get('format'));
      this.apply(); this.saveURL(false);
    }
    saveURL(push = false) {
      const url = new URL(window.location.href);
      url.searchParams.delete('format');
      for (const [name, value] of [['mushroom', this.mushroom.value], [FORMAT_PARAM, this.format.value]]) {
        if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
      }
      if (push && this.complete) url.searchParams.delete('page');
      if (url.href !== window.location.href) history[push ? 'pushState' : 'replaceState'](history.state, '', url);
      this.syncPagination(url);
    }
    syncPagination(currentURL) {
      const section = this.closest('.gbm-shop');
      if (!section) return;
      $$(section, '.gbm-pagination a[href]').forEach(link => {
        const target = new URL(link.getAttribute('href'), window.location.href);
        if (target.origin !== currentURL.origin || target.pathname !== currentURL.pathname) return;
        const page = target.searchParams.get('page');
        const params = new URLSearchParams(currentURL.search);
        if (page) params.set('page', page); else params.delete('page');
        target.search = params.toString(); link.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);
      });
    }
    apply() {
      const mushroom = this.mushroom.value, format = this.format.value;
      let count = 0;
      this.cards.forEach(card => {
        const match = (!mushroom || card.dataset.mushroom === mushroom) && (!format || card.dataset.formats.split(/\s+/).includes(format));
        card.hidden = !match;
        if (!match) return;
        count++;
        const options = $$(card, '[data-format-link]');
        options.forEach(option => { option.hidden = !!format && option.dataset.formatLink !== format; });
        const active = format ? options.find(option => option.dataset.formatLink === format) : null;
        $$(card, '[data-card-link]').forEach(link => link.setAttribute('href', active ? active.getAttribute('href') : card.original.href));
        const price = $(card, '[data-card-price]');
        if (price) price.textContent = active ? active.dataset.price : card.original.price;
        const label = $(card, '[data-card-format]');
        if (label) label.textContent = active ? (format === 'powder' ? 'Powder' : 'Capsules') : card.original.format;
        const extras = $$(card, '[data-extra-format]');
        extras.forEach(link => { link.hidden = !!format && link.dataset.extraFormat !== format; });
        const extraOptions = $(card, '[data-extra-options]');
        if (extraOptions) extraOptions.hidden = !extras.some(link => !link.hidden);
        const image = $(card, '.gbm-card-image');
        if (image) {
          if (active?.dataset.image) { image.removeAttribute('srcset'); image.src = active.dataset.image; }
          else {
            image.src = card.original.src;
            if (card.original.srcset) image.setAttribute('srcset', card.original.srcset);
          }
        }
      });
      $(this, '[data-result-count]').textContent = `${count} ${count === 1 ? 'mushroom' : 'mushrooms'}${this.complete ? '' : ' on this page'}`;
      $(this, '[data-no-results]').hidden = count !== 0 || this.cards.length === 0;
      const clear = $(this, '[data-filterbar] [data-clear-filters]');
      if (clear) clear.hidden = !(mushroom || format);
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class GbmProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.form = $(this, '.gbm-product-form');
      if (!this.form) return;
      try { this.data = JSON.parse($(this, '[data-product-data]').textContent); }
      catch (error) { console.warn('Product enhancement unavailable; native purchase form remains active.', error); return; }
      this.quantity = $(this.form, '[name=quantity]');
      this.variantInput = $(this.form, '[name=id]');
      this.plan = $(this.form, '[data-plan-select]');
      this.current = this.data.variants.find(variant => String(variant.id) === this.variantInput.value);
      this.addButton = $(this, '[data-add-button]');
      this.sticky = $(this, '[data-sticky-cart]');
      this.pending = false;
      $$(this, '[data-thumbnail]').forEach(button => button.addEventListener('click', () => {
        this.showImage(button.dataset.src, button.dataset.alt);
        $$(this, '[data-thumbnail]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
      }, {signal}));
      $(this, '[data-quantity-minus]')?.addEventListener('click', () => this.adjustQuantity(-1), {signal});
      $(this, '[data-quantity-plus]')?.addEventListener('click', () => this.adjustQuantity(1), {signal});
      this.quantity.addEventListener('change', () => this.refreshQuantityButtons(), {signal});
      this.variantInput.addEventListener('change', () => this.changeVariant(), {signal});
      this.plan?.addEventListener('change', () => this.refreshPrice(), {signal});
      this.form.addEventListener('submit', event => this.submit(event), {signal});
      this.refreshQuantityButtons(); this.refreshPrice(); this.loadFormatPairings(signal);
      if (this.sticky && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          // Do not offer a premature purchase before shoppers reach the format controls.
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        }, {threshold: 0});
        this.observer.observe(this.addButton);
      }
    }
    async loadFormatPairings(signal) {
      if (!window.GbmDiscovery || !this.dataset.catalogUrl) return;
      try {
        const catalog = await window.GbmDiscovery.ready(this.dataset.catalogUrl);
        if (signal.aborted || !this.isConnected) return;
        const group = catalog.groups.find(group => group.key === this.dataset.speciesKey);
        const picker = $(this, '[data-gbm-pairing]'), options = $(this, '[data-format-options]');
        if (!group || !picker || !options) return;
        ['capsules', 'powder'].forEach(format => {
          const choice = group.selected[format];
          if (!choice || $(options, `[data-product-format="${format}"]`)) return;
          const link = document.createElement('a'); link.className = 'gbm-format-option';
          link.href = choice.url; link.dataset.productFormat = format;
          link.textContent = format === 'capsules' ? 'Capsules' : 'Powder';
          if (this.current?.format === format) { link.classList.add('is-selected'); link.setAttribute('aria-current', 'true'); }
          options.append(link);
        });
        // A stable visual order on both capsule-first and powder-first pages.
        ['capsules', 'powder'].forEach(format => {
          const link = $(options, `[data-product-format="${format}"]`); if (link) options.append(link);
        });
        picker.hidden = options.children.length === 0;
      } catch (error) { console.warn('Additional format discovery unavailable; existing product links are retained.', error); }
    }
    adjustQuantity(direction) {
      const min = Number(this.quantity.min) || 1, step = Number(this.quantity.step) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const current = Number(this.quantity.value) || min;
      this.quantity.value = String(Math.max(min, Math.min(max, current + direction * step)));
      this.quantity.dispatchEvent(new Event('change', {bubbles: true}));
    }
    refreshQuantityButtons() {
      const min = Number(this.quantity.min) || 1, value = Number(this.quantity.value) || min;
      $(this, '[data-quantity-minus]').disabled = value <= min;
      $(this, '[data-quantity-plus]').disabled = !!this.quantity.max && value >= Number(this.quantity.max);
    }
    showImage(src, alt) {
      const image = $(this, '.gbm-product-image');
      if (!image || !src) return;
      image.removeAttribute('srcset'); image.src = src;
      if (alt) image.alt = alt;
    }
    changeVariant() {
      const variant = this.data.variants.find(item => String(item.id) === this.variantInput.value);
      if (!variant) return;
      if (this.current && variant.format !== this.current.format && ['capsules', 'powder'].includes(variant.format)) {
        // Full navigation keeps format-specific directions, media, apps and metadata consistent.
        const target = new URL(this.dataset.productUrl, window.location.origin);
        target.searchParams.set('variant', variant.id);
        window.location.assign(target.href); return;
      }
      this.current = variant;
      this.quantity.min = variant.min || 1; this.quantity.step = variant.step || 1;
      if (variant.max) this.quantity.max = variant.max; else this.quantity.removeAttribute('max');
      this.quantity.value = String(variant.min || 1);
      const size = $(this, '[data-pack-size]'); if (size) size.textContent = variant.size;
      if (variant.image) this.showImage(variant.image);
      $$(this, '[data-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.src === variant.image)));
      $$(this, '[data-product-format]').forEach(link => {
        const selected = link.dataset.productFormat === variant.format;
        link.classList.toggle('is-selected', selected);
        if (selected) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
      });
      if (this.plan) {
        [...this.plan.options].forEach(option => {
          option.disabled = !!option.value && !variant.plans.some(plan => String(plan.id) === option.value);
        });
        if (this.plan.selectedOptions[0]?.disabled) this.plan.value = [...this.plan.options].find(option => !option.disabled)?.value || '';
      }
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
      this.setBusy(false); this.refreshPrice(); this.refreshQuantityButtons();
      $(this, '[data-product-error]').hidden = true;
      $(this, '[data-form-status]').textContent = variant.available ? 'Selection updated.' : 'This option is sold out.';
    }
    refreshPrice() {
      if (!this.current) return;
      const allocation = this.plan?.value ? this.current.plans.find(plan => String(plan.id) === this.plan.value) : null;
      const price = allocation?.price || this.current.price;
      $$(this, '[data-product-price],[data-sticky-price]').forEach(element => { element.textContent = price; });
      const compare = $(this, '[data-compare-price]');
      compare.textContent = allocation ? '' : this.current.compare;
      compare.hidden = !compare.textContent;
      const unit = $(this, '[data-unit-price]');
      unit.textContent = allocation ? '' : this.current.unitPrice; unit.hidden = !unit.textContent;
    }
    setBusy(busy) {
      this.pending = busy;
      this.form.setAttribute('aria-busy', String(busy));
      $$(this, '[data-add-button],[data-sticky-add]').forEach(button => {
        button.disabled = busy || !this.current?.available;
        button.textContent = busy ? 'Adding…' : this.current?.available ? this.data.addLabel : 'Sold out';
      });
      // Keep the submitted variant stable until the server confirms the outcome.
      this.variantInput.disabled = busy;
      if (this.plan) this.plan.disabled = busy;
    }
    async submit(event) {
      event.preventDefault();
      if (this.pending || !this.current?.available || !this.form.reportValidity()) return;
      const errorBox = $(this, '[data-product-error]'), status = $(this, '[data-form-status]');
      errorBox.hidden = true; errorBox.replaceChildren(); status.textContent = '';
      const formData = new FormData(this.form); // Capture before disabling the variant select.
      formData.delete('return_to');
      this.setBusy(true);
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), 20000);
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', body: formData, credentials: 'same-origin',
          headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, signal: timeout.signal
        });
        const result = await response.json();
        if (!response.ok) {
          errorBox.textContent = typeof result.description === 'string' ? result.description : 'This item could not be added. Please check your selection and try again.';
          errorBox.hidden = false; this.setBusy(false); return;
        }
        status.textContent = 'Added to your basket.';
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        // A dropped response may follow a successful add: never silently retry the order.
        errorBox.append(document.createTextNode('We couldn’t confirm the update. Check your basket before trying again. '));
        const link = document.createElement('a'); link.href = this.dataset.cartUrl; link.textContent = 'View basket'; link.style.textDecoration = 'underline';
        errorBox.append(link); errorBox.hidden = false; this.setBusy(false);
      } finally { clearTimeout(timer); }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  for (const [name, element] of [['gbm-header', GbmHeader], ['gbm-catalog', GbmCatalog], ['gbm-product', GbmProduct]]) {
    if (!customElements.get(name)) customElements.define(name, element);
  }
  // Help links address the accordion itself; open it when a deep link is followed.
  function openHashTarget() {
    if (!location.hash) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch { return; }
    if (target instanceof HTMLDetailsElement) target.open = true;
  }
  window.addEventListener('hashchange', openHashTarget); openHashTarget();
})();
